import { Breadcrumbs } from '@bridebook/web/app-basic/components/breadcrumbs/breadcrumbs-search.container';
import { AutocompleteInputLocation } from '@bridebook/web/app-basic/components/search/location-autocomplete/autocomplete-input-location';
import GooglePredictionsContextProvider from '@bridebook/web/app-basic/components/search/location-autocomplete/context';
import { SearchIcon } from '@bridebook/web/app-basic/components/search/location-autocomplete/icons';
import LocationDescription from '@bridebook/web/app-basic/components/search/location-description/location-description';
import SearchInfoHeader from '@bridebook/web/app-basic/components/search/search-info-header/search-info-header';
import { SearchListItemLoggedOut } from '@bridebook/web/app-basic/components/search/search-list-item/search-list-item';
import SearchList from '@bridebook/web/app-basic/components/search/search-list/search-list';
import SearchLocationArticles from '@bridebook/web/app-basic/components/search/search-location-articles/search-location-articles';
import SignUpNowCard from '@bridebook/web/app-basic/components/sign-up-now-card/sign-up-now-card';
import { loadedSearchResultsOnLoggedOutPage } from '@bridebook/web/app-basic/lib/analytics/tracking-actions/viewed-search-page';
import useLoggedOutSearchBar from '@bridebook/web/app-basic/lib/hooks/use-logged-out-search-bar';
import {
  getActualCategory,
  getFAQContent,
  getFacetsContent,
  getRelatedAreas,
  getSearchFields,
  getSearchList,
  getSearchSource,
  getSearchWidgets,
} from '@bridebook/web/app-basic/lib/search/selectors';
import { useSelector } from '@bridebook/web/app-basic/lib/utils';
import partition from 'lodash/partition';
import { useTranslation } from 'next-i18next';
import { memo, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import HiddenLimitedProfilesList from '@bridebook/web/app-basic/components/search/hidden-limited-profiles-list/hidden-limited-profiles-list';
import { cn } from '@bridebook/aura';
import { useMarket } from '@bridebook/web/app-shared/lib/i18n/hooks/use-market';
import { ILocationData, ISearchPageQuery } from '@bridebook/web/app-shared/lib/search/types';
import { isCountryLevelSearch } from '@bridebook/web/app-shared/lib/search/utils/format-area';
import { formatArea } from '@bridebook/web/lib/search/utils';
import EverythingToKnow from './everything-to-know/everything-to-know';
import FAQ from './faq/faq';
import InfoWrapper from './info-wrapper/info-wrapper';
import MapWidget from './map-widget/map-widget';
import QuickFacts from './quick-facts/quick-facts';
import SearchFacets from './search-facets/search-facets';
import VideoWidget from './video-widget/video-widget';
import {
  getGoogleMapWidget,
  getVideoWidget,
  mapEverythingToKnowContent,
  mapQuickFactsContent,
} from '@bridebook/toolbox/src/seo-facets/search-widgets';
import SuppliersAroundWell from './suppliers-around-well/suppliers-around-well';
import { useHideLimitedProfiles } from '@bridebook/web/app-basic/components/search/use-hide-limited-profiles';

interface IProps {
  totalResults: number;
  fullQuery: ISearchPageQuery;
  totalPages: number;
  locationData: ILocationData | null;
  supplierFilterType?: string;
  facetsParams: string[];
  isNoResultsSearch?: boolean;
  prettyCategory: string;
}

/**
 * Main Search page for logged out users
 */
const Search = ({
  totalResults,
  fullQuery,
  totalPages,
  locationData,
  supplierFilterType,
  facetsParams,
  isNoResultsSearch,
  prettyCategory,
}: IProps) => {
  const facetsContent = useSelector(getFacetsContent);
  const fields = useSelector(getSearchFields);
  const actualCategory = useSelector(getActualCategory);
  const market = useMarket();
  const list = useSelector(getSearchList);
  const faqs = useSelector(getFAQContent);
  const searchWidgets = useSelector(getSearchWidgets);
  const relatedAreas = useSelector(getRelatedAreas);
  const dispatch = useDispatch();
  const { t } = useTranslation('loggedOutSearchBar');
  const descriptions = useMemo(() => facetsContent || locationData, [facetsContent, locationData]);

  const { onPlaceSelected, handleOnFocus, loading, showIcon, searchBarEnabled, handleOnBlur } =
    useLoggedOutSearchBar('searchPageSearchBar', actualCategory === 'venue');

  const isCountrySearch = isCountryLevelSearch(fields?.area, market.country, market.alias);

  const locationName = isCountrySearch
    ? formatArea(market.getCountryName(), market)
    : fields?.area
      ? formatArea(fields.area, market)
      : '';
  const placeholder = locationName || t('autocomplete.placeholder');

  const searchSource = useSelector(getSearchSource);

  useEffect(() => {
    dispatch(
      loadedSearchResultsOnLoggedOutPage({
        supplierCountryCode: market.country,
        searchTerm: fields?.area,
        ...(searchSource && { searchSource }),
      }),
    );
  }, [market.country, searchSource, dispatch, fields?.area]);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  const quickFacts = mapQuickFactsContent(searchWidgets);
  const everythingToKnow = mapEverythingToKnowContent(searchWidgets);
  const videoWidget = getVideoWidget(searchWidgets);
  const mapWidget = getGoogleMapWidget(searchWidgets);

  const isHideLimitedProfiles = useHideLimitedProfiles();

  const [loggedInSearch, loggedOutSearch] = useMemo(
    () => partition(list, (item) => item?.matchedBy?.includes('mockLoggedInSearch')),
    [list],
  );

  if (!fields) return null;

  return (
    <div className="bspage">
      {locationData && <Breadcrumbs locationData={locationData} slug={actualCategory} />}
      <div className="bsy">
        {searchBarEnabled && (
          <>
            <div className="bsbar">
              <GooglePredictionsContextProvider>
                <AutocompleteInputLocation
                  name="address-auto-suggest"
                  onPlaceChanged={onPlaceSelected}
                  onFocusProp={handleOnFocus}
                  onBlurProp={handleOnBlur}
                  label={t('autocomplete.modalLabel')}
                  disabled={loading}
                  placeholder={placeholder}
                  afterInput={
                    showIcon ? (
                      <div className={cn('bsicon', loading && 'bsicon-loading')}>
                        {loading ? (
                          <span className="bsspinner" />
                        ) : (
                          <SearchIcon width={16} height={16} className="m-2 text-white" />
                        )}
                      </div>
                    ) : undefined
                  }
                />
              </GooglePredictionsContextProvider>
            </div>
            <div className="bsdivider" />
          </>
        )}
        <SearchInfoHeader
          fields={fields}
          actualCategory={actualCategory}
          facetsParams={facetsParams}
          facetTitle={facetsContent?.h1}
        />
        {!supplierFilterType && <LocationDescription data={descriptions} />}
      </div>

      <SearchFacets />

      <div className="bsy">
        <SearchList
          fullQuery={fullQuery}
          totalPages={totalPages}
          facetsParams={facetsParams}
          isNoResultsSearch={isNoResultsSearch}>
          <>
            {isHideLimitedProfiles ? (
              <HiddenLimitedProfilesList
                suppliers={loggedOutSearch}
                locationData={locationData}
                locationName={locationName}
              />
            ) : (
              loggedOutSearch.map((supplier, index) => (
                <SearchListItemLoggedOut
                  key={supplier.id ?? index}
                  supplier={supplier}
                  itemIndex={index}
                />
              ))
            )}
            {loggedInSearch.length > 0 && (
              <>
                <SuppliersAroundWell
                  locationName={locationName}
                  locationData={locationData}
                  actualCategory={actualCategory}
                />
                {loggedInSearch.map((supplier, index) => (
                  <SearchListItemLoggedOut
                    key={supplier.id ?? `logged-in-${index}`}
                    supplier={supplier}
                  />
                ))}
              </>
            )}
          </>
        </SearchList>

        {actualCategory == 'venue' && (
          <div className="bsvenue">
            {everythingToKnow?.length > 0 && (
              <EverythingToKnow everythingToKnow={everythingToKnow} locationData={locationData} />
            )}
            {quickFacts?.length > 0 && (
              <QuickFacts quickFacts={quickFacts} locationData={locationData} />
            )}
          </div>
        )}

        <InfoWrapper
          data={locationData}
          prettyCategory={prettyCategory}
          relatedAreas={relatedAreas}
        />
      </div>

      {faqs?.length > 0 && (
        <div className="bsfaq">
          <FAQ faqs={faqs} />
        </div>
      )}

      <div className="bsy">
        <SignUpNowCard facets={facetsParams} totalResults={totalResults} />

        <div className="bsarticles">
          <SearchLocationArticles />
        </div>
        <VideoWidget widget={videoWidget} />
        <MapWidget widget={mapWidget} />
      </div>
    </div>
  );
};

export default memo(Search);
